import { createSlice } from "@reduxjs/toolkit";
import {
  getAllMerchants,
  getMerchantDeals,
  getMerchantProfile,
  updateMerchantProfile,
  getMerchantConfig,
  // updateMerchantConfig,
  uploadProfileImage,
  changeMerchantPassword,
  getMerchantDealProposals,
  approveDealProposal,
  rejectDealProposal,
  saveBankDetails,
  updateMerchantGeneralConfig,
  updateMerchantDeliveryConfig,
  updateMerchantHoursConfig,
  updateMerchantConfig,
} from "./MerchantThunk";
import { status } from "../../_constants/common";

const merchantSlice = createSlice({
  name: "merchant",
  initialState: {
    // All merchants list (for influencers)
    allMerchants: {
      status: null,
      data: [],
      loaded: false, // Track if data has been loaded
    },
    // Selected merchant ID
    selectedMerchantId: null,
    // Merchant profile (current user)
    merchantProfile: {
      status: null,
      data: null,
    },
    updateProfile: {
      status: null,
      data: null,
    },
    uploadProfileImage: {
      status: null,
    },
    merchantConfig: {
      status: null,
      data: null,
    },
    updateConfig: {
      status: null,
      data: null,
    },
    changePassword: {
      status: null,
      data: null,
    },

    // Bank details for payouts
    bankDetails: {
      status: null,
      data: null,
    },
    updateGeneralConfig: { status: null, data: null },
    updateDeliveryConfig: { status: null, data: null },
    updateHoursConfig: { status: null, data: null },
    // Deal proposals from influencers
    dealProposals: [],
    dealProposalsLoaded: false,
    dealProposalsPagination: null,
    dealProposalsStatusCounts: null, // Status counts from API for tabs
    dealProposalsLoading: false,
    approveProposalLoading: false,
    rejectProposalLoading: false,
    isLoading: false,
  },
  reducers: {
    // Set selected merchant
    setSelectedMerchant: (state, action) => {
      state.selectedMerchantId = action.payload;
    },
    // Clear selected merchant
    clearSelectedMerchant: (state) => {
      state.selectedMerchantId = null;
    },
    clearMerchantProfile: (state) => {
      state.merchantProfile = { status: null, data: null };
    },
    clearUpdateProfile: (state) => {
      state.updateProfile = { status: null, data: null };
    },
    clearMerchantConfig: (state) => {
      state.merchantConfig = { status: null, data: null };
    },
    clearUpdateConfig: (state) => {
      state.updateConfig = { status: null, data: null };
    },
    clearChangePassword: (state) => {
      state.changePassword = { status: null, data: null };
    },
    clearBankDetails: (state) => {
      state.bankDetails = { status: null, data: null };
    },
    clearDealProposals: (state) => {
      state.dealProposals = [];
      state.dealProposalsLoaded = false;
      state.dealProposalsPagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Merchants
      .addCase(getAllMerchants.pending, (state) => {
        state.isLoading = true;
        state.allMerchants.status = status.IN_PROGRESS;
      })
      .addCase(getAllMerchants.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.allMerchants = {
          status: status.SUCCESS,
          data: payload.map((merchant) => ({
            ...merchant,
            deals: [], // Initialize empty deals array
            dealsLoaded: false, // Track if deals are loaded for this merchant
          })),
          loaded: true,
        };
      })
      .addCase(getAllMerchants.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.allMerchants = {
          status: status.FAILURE,
          data: [],
          loaded: false,
        };
      })

      // Get Merchant Deals
      .addCase(getMerchantDeals.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getMerchantDeals.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        const { merchantId, deals } = payload;
        // Find the merchant and update its deals
        const merchantIndex = state.allMerchants.data.findIndex(
          (m) => m.id === merchantId,
        );
        if (merchantIndex !== -1) {
          state.allMerchants.data[merchantIndex].deals = deals;
          state.allMerchants.data[merchantIndex].dealsLoaded = true;
        }
      })
      .addCase(getMerchantDeals.rejected, (state) => {
        state.isLoading = false;
      })

      // Get Merchant Profile
      .addCase(getMerchantProfile.pending, (state) => {
        state.merchantProfile = {
          status: status.IN_PROGRESS,
          data: null,
        };
      })
      .addCase(getMerchantProfile.fulfilled, (state, { payload }) => {
        state.merchantProfile = {
          status: status.SUCCESS,
          data: payload?.data || payload,
        };
      })
      .addCase(getMerchantProfile.rejected, (state, { payload }) => {
        state.merchantProfile = {
          status: status.FAILURE,
          data: payload,
        };
      })

      // Update Merchant Profile
      .addCase(updateMerchantProfile.pending, (state) => {
        state.updateProfile = {
          status: status.IN_PROGRESS,
          data: null,
        };
      })
      .addCase(updateMerchantProfile.fulfilled, (state, { payload }) => {
        state.updateProfile = {
          status: status.SUCCESS,
          data: payload?.data || payload,
        };
        // Also update the merchant profile data
        if (payload?.data) {
          state.merchantProfile.data = payload.data;
        }
      })
      .addCase(updateMerchantProfile.rejected, (state, { payload }) => {
        state.updateProfile = {
          status: status.FAILURE,
          data: payload,
        };
      })

      // Get Merchant Config
      .addCase(getMerchantConfig.pending, (state) => {
        state.merchantConfig = {
          status: status.IN_PROGRESS,
          data: null,
        };
      })
      .addCase(getMerchantConfig.fulfilled, (state, { payload }) => {
        state.merchantConfig = {
          status: status.SUCCESS,
          data: payload?.data || payload,
        };
      })
      .addCase(getMerchantConfig.rejected, (state, { payload }) => {
        state.merchantConfig = {
          status: status.FAILURE,
          data: payload,
        };
      })

      // Update Merchant Config
      .addCase(updateMerchantConfig.pending, (state) => {
        state.updateConfig = {
          status: status.IN_PROGRESS,
          data: null,
        };
      })
      .addCase(updateMerchantConfig.fulfilled, (state, { payload }) => {
        state.updateConfig = {
          status: status.SUCCESS,
          data: payload?.data || payload,
        };
        // Also update the merchant config data
        if (payload?.data) {
          state.merchantConfig.data = payload.data;
        }
      })
      .addCase(updateMerchantConfig.rejected, (state, { payload }) => {
        state.updateConfig = {
          status: status.FAILURE,
          data: payload,
        };
      })

      // Change Merchant Password
      .addCase(changeMerchantPassword.pending, (state) => {
        state.changePassword = {
          status: status.IN_PROGRESS,
          data: null,
        };
      })
      .addCase(changeMerchantPassword.fulfilled, (state, { payload }) => {
        state.changePassword = {
          status: status.SUCCESS,
          data: payload?.data || payload,
        };
      })
      .addCase(changeMerchantPassword.rejected, (state, { payload }) => {
        state.changePassword = {
          status: status.FAILURE,
          data: payload,
        };
      })

      // Get Merchant Deal Proposals
      .addCase(getMerchantDealProposals.pending, (state) => {
        state.dealProposalsLoading = true;
      })
      .addCase(getMerchantDealProposals.fulfilled, (state, { payload }) => {
        state.dealProposalsLoading = false;
        state.dealProposals = payload.proposals || [];
        state.dealProposalsLoaded = true;
        state.dealProposalsPagination = payload.pagination || null;
        state.dealProposalsStatusCounts = payload.statusCounts || null; // Store status counts
      })
      .addCase(getMerchantDealProposals.rejected, (state) => {
        state.dealProposalsLoading = false;
      })

      // Approve Deal Proposal
      .addCase(approveDealProposal.pending, (state) => {
        state.approveProposalLoading = true;
      })
      .addCase(approveDealProposal.fulfilled, (state, { payload }) => {
        state.approveProposalLoading = false;
        // Update proposal in list
        state.dealProposals = state.dealProposals.map((proposal) =>
          proposal.id === payload.proposal?.id
            ? { ...proposal, status: "approved" }
            : proposal,
        );
      })
      .addCase(approveDealProposal.rejected, (state) => {
        state.approveProposalLoading = false;
      })

      // Reject Deal Proposal
      .addCase(rejectDealProposal.pending, (state) => {
        state.rejectProposalLoading = true;
      })
      .addCase(rejectDealProposal.fulfilled, (state, { payload }) => {
        state.rejectProposalLoading = false;
        // Update proposal in list
        state.dealProposals = state.dealProposals.map((proposal) =>
          proposal.id === payload.proposal?.id
            ? {
              ...proposal,
              status: "rejected",
              rejectionReason: payload.proposal.rejectionReason,
            }
            : proposal,
        );
      })
      .addCase(rejectDealProposal.rejected, (state) => {
        state.rejectProposalLoading = false;
      })

      // Save Bank Details
      .addCase(saveBankDetails.pending, (state) => {
        state.bankDetails = {
          status: status.IN_PROGRESS,
          data: null,
        };
      })
      .addCase(saveBankDetails.fulfilled, (state, { payload }) => {
        state.bankDetails = {
          status: status.SUCCESS,
          data: payload,
        };
      })
      .addCase(saveBankDetails.rejected, (state, { payload }) => {
        state.bankDetails = {
          status: status.FAILURE,
          data: payload,
        };
      })

      // general 
      .addCase(updateMerchantGeneralConfig.pending, (state) => {
        state.updateGeneralConfig.status = status.IN_PROGRESS;
      })

      .addCase(updateMerchantGeneralConfig.fulfilled, (state, { payload }) => {
        state.updateGeneralConfig.status = status.SUCCESS;
        state.merchantConfig.data = {
          ...state.merchantConfig.data,
          ...payload
        };
      })
      .addCase(updateMerchantGeneralConfig.rejected, (state) => {
        state.updateGeneralConfig.status = status.FAILURE;
      })

      // Delivery
      .addCase(updateMerchantDeliveryConfig.pending, (state) => {
        state.updateDeliveryConfig.status = status.IN_PROGRESS;
      })
      .addCase(updateMerchantDeliveryConfig.fulfilled, (state, { payload }) => {
        state.updateDeliveryConfig.status = status.SUCCESS;
        state.merchantConfig.data = {
          ...state.merchantConfig.data,
          ...payload
        };
      })
      .addCase(updateMerchantDeliveryConfig.rejected, (state) => {
        state.updateDeliveryConfig.status = status.FAILURE;
      })

      // business hours
      .addCase(updateMerchantHoursConfig.pending, (state) => {
        state.updateHoursConfig.status = status.IN_PROGRESS;
      })
      .addCase(updateMerchantHoursConfig.fulfilled, (state, { payload }) => {
        state.updateHoursConfig.status = status.SUCCESS;
        state.merchantConfig.data = {
          ...state.merchantConfig.data,
          ...payload
        };
      })
      .addCase(updateMerchantHoursConfig.rejected, (state) => {
        state.updateHoursConfig.status = status.FAILURE;
      });

    // upload profile image
    builder
      .addCase(uploadProfileImage.pending, (state) => {
        state.uploadProfileImage.status = status.IN_PROGRESS;
      })
      .addCase(uploadProfileImage.fulfilled, (state, action) => {
        state.uploadProfileImage.status = status.SUCCESS;
        // Update merchantProfile with the returned merchant data
        if (action.payload?.data?.merchant) {
          state.merchantProfile.data = {
            ...state.merchantProfile.data,
            ...action.payload.data.merchant
          };
        }
      })
      .addCase(uploadProfileImage.rejected, (state, action) => {
        state.uploadProfileImage.status = status.FAILURE;
      });

  },
});

export const {
  setSelectedMerchant,
  clearSelectedMerchant,
  clearMerchantProfile,
  clearUpdateProfile,
  clearMerchantConfig,
  clearUpdateConfig,
  clearChangePassword,
  clearBankDetails,
  clearDealProposals,
} = merchantSlice.actions;
export default merchantSlice.reducer;
